import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const CONTRA_TYPES = [
  { value:"bank_to_bank",  label:"🏦 Bank to Bank Transfer",     creditHint:"Source Bank",      debitHint:"Destination Bank" },
  { value:"cash_to_bank",  label:"💵 Cash Deposit to Bank",      creditHint:"Cash in Hand",     debitHint:"Bank Account" },
  { value:"bank_to_cash",  label:"🏧 Cash Withdrawal from Bank", creditHint:"Bank Account",     debitHint:"Cash in Hand" },
  { value:"cash_to_cash",  label:"💸 Cash to Cash Transfer",     creditHint:"Sending Cash",     debitHint:"Receiving Cash" },
  { value:"petty_topup",   label:"🪙 Petty Cash Top-up",         creditHint:"Main Bank/Cash",   debitHint:"Petty Cash Float" },
];

export default function ContraManagement() {
  const [tab,         setTab]         = useState("new");
  const [fyList,      setFyList]      = useState([]);
  const [accounts,    setAccounts]    = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [history,     setHistory]     = useState([]);
  const [balances,    setBalances]    = useState({});
  const [selectedFY,  setSelectedFY]  = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [loadingBal,  setLoadingBal]  = useState(false);
  const [contraType,  setContraType]  = useState(CONTRA_TYPES[0]);
  const [fromAccount, setFromAccount] = useState(null);
  const [toAccount,   setToAccount]   = useState(null);
  const [amount,      setAmount]      = useState("");
  const [entryDate,   setEntryDate]   = useState(new Date().toISOString().split("T")[0]);
  const [reference,   setReference]   = useState("");
  const [description, setDescription] = useState("");
  const [narration,   setNarration]   = useState("");

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (selectedFY && tab === "history") fetchHistory(); }, [selectedFY]);
  useEffect(() => { if (tab === "balances" && allAccounts.length) fetchBalances(); }, [tab, allAccounts]);

  const isCashBank = (a) =>
    a.sub_type === "cash" || a.sub_type === "bank" ||
    (a.type === "asset" && (
      a.name?.toLowerCase().includes("cash") ||
      a.name?.toLowerCase().includes("bank") ||
      a.name?.toLowerCase().includes("petty") ||
      a.account_code?.match(/^10[0-9]/)
    ));

  const fetchAll = async () => {
    const [fyRes, accRes] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.listAccounts({ postable_only: 1 }),
    ]);
    if (fyRes.status === "OK") {
      const list = fyRes.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
    if (accRes.status === "OK") {
      const all = accRes.details || [];
      setAllAccounts(all);
      setAccounts(all.filter(isCashBank));
    }
  };

  const fetchHistory = async () => {
    if (!selectedFY) return;
    setLoading(true);
    const res = await ajaxAccounting.listJournals({ type: "contra", financial_year_id: selectedFY.value });
    if (res.status === "OK") setHistory(Array.isArray(res.details) ? res.details : res.details?.list || []);
    setLoading(false);
  };

  const fetchBalances = async () => {
    if (!allAccounts.length) return;
    setLoadingBal(true);
    const cashBankAccs = allAccounts.filter(isCashBank);
    const results = {};
    await Promise.all(cashBankAccs.map(async (acc) => {
      const res = await ajaxAccounting.getAccountBalance({ id: acc.id, financial_year_id: selectedFY?.value });
      if (res.status === "OK") results[acc.id] = res.details?.balance ?? 0;
    }));
    setBalances(results);
    setLoadingBal(false);
  };

  const handleSubmit = async (andPost = false) => {
    if (!fromAccount)  { toast.error("Select the FROM account");  return; }
    if (!toAccount)    { toast.error("Select the TO account");    return; }
    if (fromAccount.value === toAccount.value) { toast.error("From and To must differ"); return; }
    if (!amount || parseFloat(amount) <= 0)    { toast.error("Enter a valid amount");   return; }
    if (!description)  { toast.error("Description required");     return; }
    if (!selectedFY)   { toast.error("Select a financial year");  return; }

    setSaving(true);
    const amt = parseFloat(amount);
    const payload = {
      type: "contra",
      entry_date: entryDate,
      reference: reference || `CTR-${Date.now()}`,
      description,
      narration,
      financial_year_id: selectedFY.value,
      lines: [
        { account_id: toAccount.value,   debit: amt, credit: 0,   description: `Transfer to ${toAccount.label}` },
        { account_id: fromAccount.value, debit: 0,   credit: amt, description: `Transfer from ${fromAccount.label}` },
      ],
    };

    const fn  = andPost ? ajaxAccounting.createAndPostJournal : ajaxAccounting.createJournal;
    const res = await fn(payload);

    if (res.status === "OK") {
      toast.success(andPost ? "Contra posted to ledger!" : "Contra saved as draft!");
      setAmount(""); setReference(""); setDescription(""); setNarration("");
      setFromAccount(null); setToAccount(null);
      fetchBalances();
      if (tab === "history") fetchHistory();
    } else {
      toast.error(res.message || "Failed.");
    }
    setSaving(false);
  };

  const acctOptions = accounts.map((a) => ({
    value: a.id, label: `${a.account_code} — ${a.name}`, balance: balances[a.id] ?? null,
  }));

  const AccountOption = ({ innerProps, label, data }) => (
    <div {...innerProps} className="px-3 py-2" style={{ cursor: "pointer" }}>
      <div className="tx-13 fw-semibold">{label}</div>
      {data.balance !== null && <div className="tx-11 tx-muted">Balance: {functions.formatCurrency(data.balance)}</div>}
    </div>
  );

  const fyOptions = fyList.map((y) => ({ value: y.id, label: y.name + (y.is_current ? " (Current)" : "") }));
  const amt = parseFloat(amount) || 0;

  const historyCols = [
    { key:"reference",   label:"Reference",  render:(v) => <code className="tx-12">{v}</code> },
    { key:"entry_date",  label:"Date",       render:(v) => functions.formatDate(v) },
    { key:"description", label:"Description",render:(v) => <span className="tx-12">{v}</span> },
    { key:"total_debit", label:"Amount",     render:(v) => <strong>{functions.formatCurrency(v)}</strong> },
    { key:"status",      label:"Status",     render:(v) => <span className={`badge bg-${v==="posted"?"success":"secondary"}`}>{v}</span> },
  ];

  const cashBankAccounts = allAccounts.filter(isCashBank);
  const totalFunds = cashBankAccounts.reduce((s, a) => s + (balances[a.id] || 0), 0);

  return (
    <AppContainer
      title="Contra Management"
      previousPageTitle="Journal Entries"
      description="Transfer funds between cash and bank accounts. Contra entries keep both sides balanced — one account is debited, the other credited, for the same amount."
    >
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} isSearchable />
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs mb-0">
        {[
          { key:"new",      label:"New Contra Entry", icon:"fa-plus-circle" },
          { key:"history",  label:"History",          icon:"fa-list" },
          { key:"balances", label:"Account Balances", icon:"fa-wallet" },
        ].map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${tab===t.key?"active":""}`}
              onClick={() => { setTab(t.key); if(t.key==="history") fetchHistory(); if(t.key==="balances") fetchBalances(); }}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── NEW ENTRY ─────────────────────────────────────────────────── */}
      {tab === "new" && (
        <div className="card custom-card p-4" style={{ borderTopLeftRadius: 0 }}>
          <div className="row">
            <div className="col-lg-7">
              <h6 className="fw-semibold mb-3">New Contra / Transfer Entry</h6>

              <div className="mb-3">
                <label className="form-label tx-12 fw-semibold">Transfer Type</label>
                <div className="d-flex flex-column gap-2">
                  {CONTRA_TYPES.map((ct) => (
                    <div key={ct.value} onClick={() => setContraType(ct)}
                      className="p-2 rounded d-flex align-items-center gap-2"
                      style={{ border:`2px solid ${contraType.value===ct.value?"#4f46e5":"#e5e7eb"}`, background:contraType.value===ct.value?"#eef2ff":"#fff", cursor:"pointer" }}>
                      <i className="fa-solid fa-circle-dot" style={{ color:contraType.value===ct.value?"#4f46e5":"#d1d5db" }}></i>
                      <span className="tx-13 fw-semibold">{ct.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="row g-3 mt-1">
                <div className="col-12">
                  <label className="form-label tx-12 fw-semibold">
                    <span className="badge bg-danger me-1">CR</span>{contraType.creditHint} (money leaves here) *
                  </label>
                  <Select options={acctOptions} value={fromAccount} onChange={setFromAccount}
                    placeholder="Select account..." isSearchable isClearable components={{ Option: AccountOption }} />
                  {fromAccount && balances[fromAccount.value] !== undefined && (
                    <div className="tx-11 mt-1">
                      Balance: <strong className={balances[fromAccount.value] >= amt ? "tx-success" : "tx-danger"}>
                        {functions.formatCurrency(balances[fromAccount.value])}
                      </strong>
                      {amt > 0 && balances[fromAccount.value] < amt && <span className="badge bg-danger ms-2 tx-10">⚠ Insufficient</span>}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label tx-12 fw-semibold">
                    <span className="badge bg-success me-1">DR</span>{contraType.debitHint} (money arrives here) *
                  </label>
                  <Select options={acctOptions.filter((o) => o.value !== fromAccount?.value)}
                    value={toAccount} onChange={setToAccount}
                    placeholder="Select account..." isSearchable isClearable components={{ Option: AccountOption }} />
                  {toAccount && balances[toAccount.value] !== undefined && (
                    <div className="tx-11 mt-1 tx-muted">
                      Current balance: <strong>{functions.formatCurrency(balances[toAccount.value])}</strong>
                      {amt > 0 && <span className="ms-2 tx-success">→ Will become: {functions.formatCurrency((balances[toAccount.value]||0)+amt)}</span>}
                    </div>
                  )}
                </div>

                <div className="col-lg-6">
                  <label className="form-label tx-12 fw-semibold">Transfer Amount (UGX) *</label>
                  <div className="input-group">
                    <span className="input-group-text fw-semibold">UGX</span>
                    <input type="number" className="form-control" placeholder="0.00" min="1" step="0.01"
                      value={amount} onChange={(e) => setAmount(e.target.value)} style={{ fontSize:"1.1rem",fontWeight:600 }} />
                  </div>
                  {amt > 0 && <div className="tx-11 tx-muted mt-1">{new Intl.NumberFormat("en-UG").format(amt)} UGX</div>}
                </div>

                <div className="col-lg-6">
                  <label className="form-label tx-12 fw-semibold">Date</label>
                  <input type="date" className="form-control" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
                </div>

                <div className="col-lg-6">
                  <label className="form-label tx-12 fw-semibold">Reference</label>
                  <input className="form-control" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Cheque #, EFT # ..." />
                </div>

                <div className="col-12">
                  <label className="form-label tx-12 fw-semibold">Description *</label>
                  <input className="form-control" value={description} onChange={(e) => setDescription(e.target.value)}
                    placeholder={`e.g. ${contraType.label}`} />
                </div>

                <div className="col-12">
                  <label className="form-label tx-12 fw-semibold">Narration / Notes</label>
                  <textarea className="form-control" rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} />
                </div>
              </div>

              <div className="d-flex gap-2 mt-4">
                <button className="btn btn-primary" onClick={() => handleSubmit(true)} disabled={saving}>
                  <i className="fa-solid fa-check-double me-1"></i>{saving ? "Posting..." : "Post to Ledger"}
                </button>
                <button className="btn btn-outline-secondary" onClick={() => handleSubmit(false)} disabled={saving}>
                  <i className="fa-solid fa-floppy-disk me-1"></i>Save Draft
                </button>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="card p-3 mb-3" style={{ border:"1px solid #e5e7eb", borderRadius:"12px" }}>
                <h6 className="fw-semibold mb-3">Entry Preview</h6>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="text-center" style={{ flex:1 }}>
                    <div style={{ padding:"12px", borderRadius:"10px", background:"#fee2e2", border:"2px solid #fca5a5" }}>
                      <i className="fa-solid fa-circle-arrow-up tx-danger" style={{ fontSize:"1.2rem" }}></i>
                      <div className="tx-11 fw-bold mt-1 tx-danger">Credit (Out)</div>
                      <div className="tx-12 fw-semibold mt-1">{fromAccount?.label?.split("—")[1]?.trim() || "Select..."}</div>
                    </div>
                  </div>
                  <div className="text-center px-2">
                    <div style={{ background:"#4f46e5",color:"#fff",borderRadius:"50%",width:"36px",height:"36px",display:"flex",alignItems:"center",justifyContent:"center" }}>
                      <i className="fa-solid fa-arrow-right"></i>
                    </div>
                    {amt > 0 && <div className="tx-11 fw-bold mt-1 tx-primary">{functions.formatCurrency(amt)}</div>}
                  </div>
                  <div className="text-center" style={{ flex:1 }}>
                    <div style={{ padding:"12px", borderRadius:"10px", background:"#d1fae5", border:"2px solid #6ee7b7" }}>
                      <i className="fa-solid fa-circle-arrow-down tx-success" style={{ fontSize:"1.2rem" }}></i>
                      <div className="tx-11 fw-bold mt-1 tx-success">Debit (In)</div>
                      <div className="tx-12 fw-semibold mt-1">{toAccount?.label?.split("—")[1]?.trim() || "Select..."}</div>
                    </div>
                  </div>
                </div>
                <div style={{ background:"#f8fafc", borderRadius:"8px", padding:"12px" }}>
                  <div className="tx-11 fw-semibold tx-muted mb-2">JOURNAL ENTRY PREVIEW</div>
                  <div className="d-flex justify-content-between tx-12 mb-1">
                    <span>Dr {toAccount?.label?.split("—")[1]?.trim() || "..."}</span>
                    <span className="fw-bold">{functions.formatCurrency(amt)}</span>
                  </div>
                  <div className="d-flex justify-content-between tx-12 tx-muted">
                    <span className="ms-3">Cr {fromAccount?.label?.split("—")[1]?.trim() || "..."}</span>
                    <span>{functions.formatCurrency(amt)}</span>
                  </div>
                </div>
              </div>
              <div className="alert alert-info tx-12">
                <strong><i className="fa-solid fa-lightbulb me-1"></i>Accounting Rule:</strong>
                <p className="mb-1 mt-1">Money leaving → <strong className="tx-danger">Credited</strong></p>
                <p className="mb-0">Money entering → <strong className="tx-success">Debited</strong></p>
                <hr className="my-2" />
                <span className="tx-11">Both sides are cash or bank accounts. The single amount is entered once — the system creates both sides automatically.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY ────────────────────────────────────────────────────── */}
      {tab === "history" && (
        <div className="card custom-card p-4" style={{ borderTopLeftRadius:0 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-semibold mb-0">Contra History</h6>
            <ExportBar data={history} columns={[{key:"reference",label:"Ref"},{key:"entry_date",label:"Date"},{key:"description",label:"Desc"},{key:"total_debit",label:"Amount"},{key:"status",label:"Status"}]}
              filename="contra-history" pdfTitle="Contra History" printCode="" exportCode="" />
          </div>
          <DataTable columns={historyCols} data={history} loading={loading} pageSize={15} emptyMessage="No contra entries for this financial year." />
        </div>
      )}

      {/* ── BALANCES ───────────────────────────────────────────────────── */}
      {tab === "balances" && (
        <div className="card custom-card p-4" style={{ borderTopLeftRadius:0 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="fw-semibold mb-0">Cash & Bank Balances</h6>
              <span className="tx-12 tx-muted">{selectedFY?.label}</span>
            </div>
            <div className="card p-2 border-0 bg-light">
              <div className="tx-11 tx-muted">Total Cash & Bank</div>
              <div className="fw-bold tx-18 tx-primary">{functions.formatCurrency(totalFunds)}</div>
            </div>
          </div>
          {loadingBal ? <div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div> : (
            <div
              // A grid with real gutters. `row g-3` gives none here — the
              // template ships a Bootstrap without the gap utilities, so the
              // cards sat edge to edge.
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))",
                gap: 16,
              }}
            >
              {cashBankAccounts.map((acc) => {
                const bal = balances[acc.id] ?? 0;
                const isBank = acc.sub_type==="bank"||acc.name?.toLowerCase().includes("bank");
                const color  = isBank ? "#6366f1" : "#10b981";
                const icon   = isBank ? "fa-building-columns" : "fa-money-bills";
                return (
                  <div key={acc.id}>
                    {/* No coloured stripe down the side — the icon and the
                        figure already say whether this is cash or a bank, and
                        the stripe only cut the row up. The card carries its own
                        spacing rather than relying on `gap`, which this
                        template's trimmed Bootstrap does not ship. */}
                    <div
                      className="card h-100"
                      style={{
                        border: "1px solid #e9ebf1",
                        borderRadius: "12px",
                        padding: "16px 18px 14px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start" style={{ gap: 12, marginBottom: 12 }}>
                        <div style={{ minWidth: 0 }}>
                          <div className="tx-11 tx-muted text-uppercase">{isBank?"Bank":"Cash"}</div>
                          <div className="fw-semibold tx-13" style={{ margin: "2px 0 3px", lineHeight: 1.35 }}>{acc.name}</div>
                          <code className="tx-11 tx-muted">{acc.account_code}</code>
                        </div>
                        <div style={{ flex:"0 0 auto", width:"36px",height:"36px",borderRadius:"9px",background:color+"18",display:"flex",alignItems:"center",justifyContent:"center" }}>
                          <i className={`fa-solid ${icon}`} style={{ color, fontSize:"1rem" }}></i>
                        </div>
                      </div>
                      <div className="fw-bold tx-18" style={{ color, marginBottom: 14 }}>{functions.formatCurrency(bal)}</div>
                      <div style={{ marginTop: "auto", display: "flex", gap: 8 }}>
                        <button className="btn btn-sm btn-outline-secondary tx-11" style={{ flex: "1 1 0" }}
                          onClick={() => { setTab("new"); setFromAccount({ value:acc.id, label:`${acc.account_code} — ${acc.name}` }); }}>
                          Transfer Out
                        </button>
                        <button className="btn btn-sm btn-outline-primary tx-11" style={{ flex: "1 1 0" }}
                          onClick={() => { setTab("new"); setToAccount({ value:acc.id, label:`${acc.account_code} — ${acc.name}` }); }}>
                          Receive
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </AppContainer>
  );
}
